import { isDynamicPortraitMaterialCategory } from '@/features/material-library/targets'
import { Category } from '@/material/web/category'
import { getMaterialPreviewUrl, type MaterialGoods, type MaterialResource } from '@/material/web/api'

/** 素材路径可识别的分辨率档位（顺序对齐工具 3.0 素材库下载弹窗）。 */
const KNOWN_MATERIAL_SIZE_ORDER = ['540', '720', '1080', '600', '960', '1280', 'l2'] as const
const KNOWN_MATERIAL_SIZE_SET = new Set<string>(KNOWN_MATERIAL_SIZE_ORDER)
/** 换装立绘预览分辨率展示顺序（对齐工具 3.0 PreviewHalf）。 */
const VERTICAL_DOLL_MATERIAL_SIZE_ORDER = ['540', '720', 'l2'] as const
const VERTICAL_MATERIAL_SIZE_TOKENS = new Set(['600', '960', '1280'])
/** 无显式 size 时平面预览的默认档位。 */
export const MATERIAL_PREVIEW_DISPLAY_SIZE = '540'

/** 素材路径 token 对应的实际分辨率文案（宽×高）。 */
const MATERIAL_SIZE_RESOLUTION_LABELS: Record<string, string> = {
  '540': '960×540',
  '720': '1280×720',
  '1080': '1920×1080',
  '600': '600×800',
  '960': '540×960',
  '1280': '720×1280',
  'l2': '原图',
}

/** 列表卡片展示模式。 */
export type MaterialCardMode = 'vertical' | 'horizontal' | 'music'

/** 按顶部分类 ID 推导素材卡片展示模式。 */
export function resolveMaterialCardMode(topCategoryId: string): MaterialCardMode {
  if ([Category.音乐, Category.音效].includes(topCategoryId as Category)) return 'music'
  if ([Category.背景, Category.CG, Category.UI, Category.特效].includes(topCategoryId as Category)) {
    return 'horizontal'
  }
  return 'vertical'
}

export interface MaterialPreviewSizeOption {
  label: string
  value: string
}

export interface MaterialDollPartGroup {
  key: string
  previewOptions: MaterialResource[]
  resourceMap: Record<string, MaterialResource>
}

export interface MaterialDollBundle {
  availableSizes: string[]
  bodies: MaterialResource[]
  bodyMap: Record<string, MaterialResource>
  partKeys: string[]
  parts: Record<string, MaterialDollPartGroup>
  resourceOrder: Record<string, number>
}

export function getResourceExtra(resource: MaterialResource | null | undefined): string {
  return resource?.extra ?? ''
}

export function isMusicCategory(topCategoryId: string): boolean {
  return [Category.音乐, Category.音效].includes(topCategoryId as Category)
}

export function isHorizontalCategory(topCategoryId: string): boolean {
  return [Category.背景, Category.CG, Category.UI, Category.特效].includes(topCategoryId as Category)
}

export function isVerticalPreviewCategory(topCategoryId: string): boolean {
  return (
    [Category.推荐, Category.人物立绘, Category.辅助立绘, Category.以爱之名, Category.偶像区].includes(
      topCategoryId as Category,
    ) || isDynamicPortraitMaterialCategory(topCategoryId)
  )
}

/** 背景、CG 等横版分类是否走换装合成预览（对齐工具 3.0 Advanced）。 */
export function isBackgroundCgCompositeCategory(topCategoryId: string): boolean {
  return topCategoryId === Category.背景 || topCategoryId === Category.CG
}

/** 列表/预览区用的素材资源稳定 key（避免用数组下标作 key）。 */
export function buildMaterialResourceStableKey(resource: MaterialResource, index = 0): string {
  return [String(resource.id ?? ''), resource.resmd5, resource.extra, String(index)].join('|')
}

/**
 * 是否使用换装合成预览与应用（立绘竖版，或背景/CG 且含身体分层结构）。
 */
export function shouldUseDollCompositePreview(
  topCategoryId: string,
  goods: MaterialGoods | null,
  resources: MaterialResource[],
): boolean {
  if (goods?.hasDoll !== '1') return false
  const bundle = buildDollBundle(resources, topCategoryId)
  if (!bundle.bodies.length && !bundle.partKeys.length) return false
  if (isVerticalPreviewCategory(topCategoryId)) return true
  return isBackgroundCgCompositeCategory(topCategoryId)
}

/**
 * 由横版方案缩略图解析对应身体底图（`0_身体\f_small_<id>` → `540_<id>`）。
 */
export function resolveDollBodyResourceFromThumb(
  resources: MaterialResource[],
  thumb: MaterialResource | null,
): MaterialResource | null {
  if (!thumb) return null
  const extra = getResourceExtra(thumb)
  const bodyId = extra.match(/0_身体\\f_small_(\d+)(?=_.+)/)?.[1]
  if (bodyId) {
    const matched = resources.find((item) => {
      const itemExtra = getResourceExtra(item)
      return itemExtra.includes('身体\\') && itemExtra.includes(`540_${bodyId}`)
    })
    if (matched) return matched
  }
  const bundle = buildDollBundle(resources)
  return bundle.bodies[0] ?? null
}

function isInvalidMaterialSizeToken(value: string): boolean {
  return !value || value === 'f' || value === 'f_small' || value === '0'
}

function orderMaterialSizes(sizes: Iterable<string>): string[] {
  const set = new Set(sizes)
  return KNOWN_MATERIAL_SIZE_ORDER.filter((size) => set.has(size))
}

/** 平面素材首部尺寸 token（对齐工具 3.0 `^\w+(?=_\d+_\D)`）。 */
function extractFlatMaterialSizeToken(extra: string): string {
  if (!extra) return ''
  const head = extra.match(/^(\w+)(?=_\d+_\D)/)?.[1] ?? ''
  return head && !isInvalidMaterialSizeToken(head) ? head : ''
}

/** 背景/CG 路径首部尺寸 token（对齐工具 3.0 `extra.split('_')[0]`）。 */
function extractBackgroundMaterialSizeToken(extra: string): string {
  if (!extra) return ''
  const head = extra.split('_')[0] ?? ''
  return head && KNOWN_MATERIAL_SIZE_SET.has(head) ? head : ''
}

/** 特效素材尺寸 token（对齐工具 3.0 gif 路径 `_尺寸_序号.ext`）。 */
function extractEffectMaterialSizeToken(extra: string): string {
  if (!extra) return ''
  const match = extra.match(/_(\d+|l2)(?=_\d+\.[^.]+$)/i)
  const size = match?.[1] ?? ''
  return size && KNOWN_MATERIAL_SIZE_SET.has(size) ? size : ''
}

/**
 * UI 素材可用档位：仅解析 `.oui` 主文件路径中的尺寸 token（对齐工具 3.0 ModalDownload）。
 * 不从预览图路径推断，避免横竖屏档位同时出现。
 */
function extractUiMaterialSizeTokens(resources: MaterialResource[]): string[] {
  const ouiResource = resources.find((item) => getResourceExtra(item).toLowerCase().endsWith('.oui'))
  if (!ouiResource) return []
  const extra = getResourceExtra(ouiResource)
  const match = extra.match(/\d+_\d+_(\d+)_.+\.oui$/i)
  const size = match?.[1] ?? ''
  if (!size || !KNOWN_MATERIAL_SIZE_SET.has(size)) return []
  return [size]
}

/**
 * 从单条资源 extra 提取尺寸 token；按顶部分类使用与工具 3.0 一致的路径规则。
 */
function extractMaterialSizeTokensForResource(
  resource: MaterialResource,
  topCategoryId: string,
): string[] {
  const extra = getResourceExtra(resource)
  if (!extra) return []

  if (topCategoryId === Category.UI) {
    return []
  }

  if (topCategoryId === Category.特效) {
    const size = extractEffectMaterialSizeToken(extra)
    return size ? [size] : []
  }

  if (isVerticalPreviewCategory(topCategoryId)) {
    const tokens: string[] = []
    const dollSize = extractDollSize(extra)
    if (dollSize && KNOWN_MATERIAL_SIZE_SET.has(dollSize) && !isInvalidMaterialSizeToken(dollSize)) {
      tokens.push(dollSize)
    }
    // 单张立绘（540_xxx）与换装（\540\）并存，对齐工具 3.0 下载弹窗平面前缀解析。
    const flatSize = extractFlatMaterialSizeToken(extra)
    if (flatSize && !tokens.includes(flatSize)) {
      tokens.push(flatSize)
    }
    return tokens
  }

  const tokens: string[] = []
  const dollSize = extractDollSize(extra)
  if (dollSize && KNOWN_MATERIAL_SIZE_SET.has(dollSize) && !isInvalidMaterialSizeToken(dollSize)) {
    tokens.push(dollSize)
  }

  if (topCategoryId === Category.背景 || topCategoryId === Category.CG) {
    const bgSize = extractBackgroundMaterialSizeToken(extra)
    if (bgSize) tokens.push(bgSize)
    return tokens
  }

  const flatSize = extractFlatMaterialSizeToken(extra)
  if (flatSize) tokens.push(flatSize)
  return tokens
}

/**
 * 从素材资源路径收集 validSizes（对齐工具 3.0 ModalDownload.validSizes）。
 * 仅返回数据中实际存在的档位，不做分类预设补全。
 */
export function collectMaterialValidSizes(
  resources: MaterialResource[],
  topCategoryId = '',
): string[] {
  if (topCategoryId === Category.UI) {
    return orderMaterialSizes(extractUiMaterialSizeTokens(resources))
  }

  const valid = new Set<string>()
  for (const resource of resources) {
    for (const size of extractMaterialSizeTokensForResource(resource, topCategoryId)) {
      valid.add(size)
    }
  }
  return orderMaterialSizes(valid)
}

function resolveMaterialSizeDisplayOrder(topCategoryId: string): readonly string[] {
  if (isVerticalPreviewCategory(topCategoryId)) {
    return VERTICAL_DOLL_MATERIAL_SIZE_ORDER
  }
  return KNOWN_MATERIAL_SIZE_ORDER
}

/**
 * 构建分辨率下拉：仅展示素材包内实际有的档位，文案为实际分辨率。
 */
export function buildMaterialSizeOptions(
  validSizes: string[],
  topCategoryId: string,
  options: { includeL2?: boolean } = {},
): MaterialPreviewSizeOption[] {
  const includeL2 = options.includeL2 !== false
  const validSet = new Set(validSizes)
  return resolveMaterialSizeDisplayOrder(topCategoryId)
    .filter((value) => validSet.has(value) && (includeL2 || value !== 'l2'))
    .map((value) => ({
      value,
      label: MATERIAL_SIZE_RESOLUTION_LABELS[value] ?? value,
    }))
}

function extractDollPartKey(extra: string): string {
  return extra.match(/\d_(.+?)(?=_?\w*\\)/)?.[1] ?? ''
}

function extractDollPartPreviewId(extra: string): string {
  return extra.match(/f_small_(\d+)(?=_.+)/)?.[1] ?? ''
}

function extractDollSize(extra: string): string {
  return extra.match(/\\(\w+)(?=_\d+_)/)?.[1] ?? ''
}

/** 按当前工程分辨率从素材包可用尺寸中选择默认应用尺寸。 */
export function resolvePreferredMaterialSize(
  sizes: string[],
  screenWidth: number,
  screenHeight: number,
): string {
  if (!sizes.length) return ''
  const width = Math.max(1, screenWidth || 1280)
  const height = Math.max(1, screenHeight || 720)
  if (height > width) {
    const preferredVertical = height <= 600 ? '600' : height <= 960 ? '960' : '1280'
    if (sizes.includes(preferredVertical)) return preferredVertical
    const vertical = sizes.find((size) => VERTICAL_MATERIAL_SIZE_TOKENS.has(size))
    if (vertical) return vertical
    return sizes[0] ?? ''
  }
  if (width === 800 && height === 600 && sizes.includes('600')) return '600'
  const shortSide = Math.min(width, height)
  const preferred = shortSide <= 540 ? '540' : shortSide <= 720 ? '720' : '1080'
  if (sizes.includes(preferred)) return preferred
  if (preferred === '1080' && sizes.includes('720')) return '720'
  if ((preferred === '1080' || preferred === '720') && sizes.includes('540')) return '540'
  return sizes[0] ?? ''
}

/** 按当前工程分辨率匹配素材尺寸；没有精确匹配时返回空，避免应用错分辨率资源。 */
export function resolveMatchedMaterialSize(
  sizes: string[],
  screenWidth: number,
  screenHeight: number,
): string {
  if (!sizes.length) return ''
  const width = Math.max(1, screenWidth || 1280)
  const height = Math.max(1, screenHeight || 720)
  if (height > width) {
    const matchedVertical = height <= 600 ? '600' : height <= 960 ? '960' : '1280'
    return sizes.includes(matchedVertical) ? matchedVertical : ''
  }
  if (width === 800 && height === 600 && sizes.includes('600')) return '600'
  const shortSide = Math.min(width, height)
  const matched = shortSide <= 540 ? '540' : shortSide <= 720 ? '720' : '1080'
  return sizes.includes(matched) ? matched : ''
}

/**
 * 同步素材预览分辨率下拉选中值：优先工程匹配档，否则保留用户已选且仍有效的档位，再否则取第一项。
 */
export function resolveMaterialPreviewSelectedSize(
  sizes: string[],
  preferred: string,
  current: string,
): string {
  if (!sizes.length) return ''
  if (preferred && sizes.includes(preferred)) return preferred
  if (current && sizes.includes(current)) return current
  return sizes[0] ?? ''
}

export function getFlatAssetThumbs(resources: MaterialResource[]): MaterialResource[] {
  const thumbs = resources.filter((item) => {
    const extra = getResourceExtra(item)
    return !!item.resmd5 && (extra.startsWith('f_small') || extra.startsWith('0_身体\\f_small'))
  })
  return thumbs.length ? thumbs : resources.filter((item) => !!item.resmd5)
}

/**
 * 平面素材缩略图：无分辨率标记的原图直接保留；有多档位时仅保留至少可解析一档的缩略图。
 */
export function getResolvableFlatAssetThumbs(
  resources: MaterialResource[],
  topCategoryId: string,
): MaterialResource[] {
  const thumbs = getFlatAssetThumbs(resources)
  if (!collectMaterialValidSizes(resources, topCategoryId).length) {
    return thumbs.filter((item) => !!item.resmd5)
  }
  return thumbs.filter((thumb) => resolveThumbSizeOptions(resources, topCategoryId, thumb).length > 0)
}

/**
 * 当前缩略图可用的分辨率下拉项；无缩略图时回退到素材包级可用档位。
 */
export function resolveThumbSizeOptions(
  resources: MaterialResource[],
  topCategoryId: string,
  thumb: MaterialResource | null,
): MaterialPreviewSizeOption[] {
  const packageOptions = buildMaterialSizeOptions(collectMaterialValidSizes(resources, topCategoryId), topCategoryId)
  if (!thumb) return packageOptions
  if (!packageOptions.length) return []
  return packageOptions.filter(
    (item) => !!resolveFlatAssetPreviewResource(resources, topCategoryId, thumb, item.value)?.resmd5,
  )
}

/**
 * 按尺寸解析换装身体底图（对齐工具 3.0 `selectedBody.extra.replace('540', size)`）。
 */
export function resolveDollBodyResourceAtSize(
  bundle: MaterialDollBundle,
  selectedBody: MaterialResource | null,
  size: string,
): MaterialResource | null {
  if (!selectedBody || !size) return null
  const bodyKey = getResourceExtra(selectedBody).replace('540', size)
  return bundle.bodyMap[bodyKey] ?? null
}

/**
 * 判断换装合成在指定档位下是否可解析：身体与每个已选部位均须有该档真实资源。
 */
function isDollCompositeResolvableAtSize(
  bundle: MaterialDollBundle,
  selectedBody: MaterialResource | null,
  selectedPartIds: Record<string, string>,
  size: string,
): boolean {
  if (!resolveDollBodyResourceAtSize(bundle, selectedBody, size)) return false
  for (const key of bundle.partKeys) {
    const selectedId = selectedPartIds[key]
    if (!selectedId) continue
    if (!resolveDollPartResource(bundle, key, selectedId, size)) return false
  }
  return true
}

/**
 * 换装合成可用档位：仅保留当前身体与已选部位均能在该档落到真实资源的尺寸（对齐工具 3.0 下载校验）。
 */
export function collectDollCompositeResolvableSizes(
  bundle: MaterialDollBundle,
  selectedBody: MaterialResource | null,
  selectedPartIds: Record<string, string>,
): string[] {
  return orderMaterialSizes(
    bundle.availableSizes.filter((size) =>
      isDollCompositeResolvableAtSize(bundle, selectedBody, selectedPartIds, size),
    ),
  )
}

/** 换装部位预览项：至少在一个可用档位下能解析到资源才展示。 */
export function filterDollPartPreviewOptions(
  bundle: MaterialDollBundle,
  partKey: string,
  sizeValues: string[],
): MaterialResource[] {
  const group = bundle.parts[partKey]
  if (!group) return []
  if (!sizeValues.length) return group.previewOptions
  return group.previewOptions.filter((item) => {
    const previewId = extractPartPreviewId(item)
    if (!previewId) return false
    return sizeValues.some((size) => !!resolveDollPartResource(bundle, partKey, previewId, size))
  })
}

/** 横版换装方案缩略图：至少有一个档位能解析到身体底图才展示。 */
export function filterResolvableSchemeThumbs(
  resources: MaterialResource[],
  bundle: MaterialDollBundle,
  _topCategoryId: string,
  thumbs: MaterialResource[],
): MaterialResource[] {
  return thumbs.filter((thumb) => {
    const body = resolveDollBodyResourceFromThumb(resources, thumb)
    if (!body) return false
    return collectDollCompositeResolvableSizes(bundle, body, {}).length > 0
  })
}

/**
 * 音乐曲目列表：排除 `f_` 开头的辅助文件，与工具 3.0 PreviewMusic 一致。
 */
export function getMusicResources(resources: MaterialResource[]): MaterialResource[] {
  return resources.filter((item) => !/^f_/.test(getResourceExtra(item)))
}

/**
 * 音乐/音效曲目展示名：取 `extra` 末级文件名并去掉扩展名。
 */
export function getMusicResourceDisplayName(resource: MaterialResource): string {
  const extra = getResourceExtra(resource).trim()
  if (!extra) return ''
  const fileName = extra.replace(/\\/g, '/').split('/').pop() ?? extra
  return fileName.replace(/\.\w+$/, '') || fileName
}

/**
 * 读取素材库音频时长（秒）；接口字段 `music_time`，无效时返回 0。
 */
export function readMaterialMusicDurationSeconds(resource: MaterialResource | null | undefined): number {
  const raw = Math.round(Number(resource?.music_time) || 0)
  return raw > 0 ? raw : 0
}

/**
 * 将秒数格式化为 `mm:ss`；无效时返回 `--:--`。
 */
export function formatMaterialMusicDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '--:--'
  const total = Math.floor(seconds)
  const mm = Math.floor(total / 60)
  const ss = total % 60
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
}

export function buildDollBundle(resources: MaterialResource[], topCategoryId = ''): MaterialDollBundle {
  const bodyMap: Record<string, MaterialResource> = {}
  const partBuckets: Record<string, Record<string, MaterialResource>> = {}
  const resourceOrder: Record<string, number> = {}
  const bodies: MaterialResource[] = []

  for (const [index, resource] of resources.entries()) {
    const extra = getResourceExtra(resource)
    resourceOrder[extra] = index

    if (extra.startsWith('0_身体\\540')) {
      bodies.push(resource)
    }
    if (extra.includes('身体\\')) {
      bodyMap[extra] = resource
    }

    const key = extractDollPartKey(extra)
    if (!key) continue
    if (!partBuckets[key]) {
      partBuckets[key] = {}
    }
    partBuckets[key]![extra] = resource
  }

  const partKeys = Object.keys(partBuckets).filter((key) => key !== '身体')
  const parts: Record<string, MaterialDollPartGroup> = {}
  for (const key of partKeys) {
    const resourceMap = partBuckets[key] ?? {}
    parts[key] = {
      key,
      previewOptions: Object.values(resourceMap).filter((item) => getResourceExtra(item).includes('f_small')),
      resourceMap,
    }
  }

  const availableSizes = collectMaterialValidSizes(resources, topCategoryId)
  return {
    availableSizes,
    bodies: bodies.length
      ? bodies
      : resources.filter(
          (item) => getResourceExtra(item).includes('身体\\') && !getResourceExtra(item).includes('f_small'),
        ),
    bodyMap,
    partKeys,
    parts,
    resourceOrder,
  }
}

export function extractPartPreviewId(resource: MaterialResource): string {
  return extractDollPartPreviewId(getResourceExtra(resource))
}

function getDollExtraLayerOrder(resource: MaterialResource): number {
  const value = Number.parseInt(getResourceExtra(resource).match(/^-?\d+(?=_)/)?.[0] ?? '', 10)
  return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER
}

export function resolveDollPartResource(
  bundle: MaterialDollBundle,
  key: string,
  selectedId: string,
  size: string,
): MaterialResource | null {
  const group = bundle.parts[key]
  if (!group) return null
  const match = Object.keys(group.resourceMap).find((extra) => extra.includes(`${size}_${selectedId}`))
  return match ? group.resourceMap[match] ?? null : null
}

function getSelectedDollPartResources(
  bundle: MaterialDollBundle,
  selectedPartIds: Record<string, string>,
  size: string,
): MaterialResource[] {
  const resources: MaterialResource[] = []
  for (const key of bundle.partKeys) {
    const selectedId = selectedPartIds[key]
    if (!selectedId) continue
    const resource = resolveDollPartResource(bundle, key, selectedId, size)
    if (!resource) continue
    resources.push(resource)
  }
  return resources
}

function sortDollResourcesBySourceOrder(bundle: MaterialDollBundle, resources: MaterialResource[]): MaterialResource[] {
  return [...resources].sort(
    (a, b) => {
      const layerDiff = getDollExtraLayerOrder(a) - getDollExtraLayerOrder(b)
      if (layerDiff !== 0) return layerDiff
      return (bundle.resourceOrder[getResourceExtra(a)] ?? Number.MAX_SAFE_INTEGER)
        - (bundle.resourceOrder[getResourceExtra(b)] ?? Number.MAX_SAFE_INTEGER)
    },
  )
}

function getDollLayerResources(
  bundle: MaterialDollBundle,
  selectedBody: MaterialResource | null,
  selectedPartIds: Record<string, string>,
  size: string,
): MaterialResource[] {
  const resources: MaterialResource[] = []
  const bodyResource = resolveDollBodyResourceAtSize(bundle, selectedBody, size)
  if (bodyResource) {
    resources.push(bodyResource)
  }
  resources.push(...getSelectedDollPartResources(bundle, selectedPartIds, size))
  return sortDollResourcesBySourceOrder(bundle, resources)
}

export function getDollLayerUrls(
  bundle: MaterialDollBundle,
  selectedBody: MaterialResource | null,
  selectedPartIds: Record<string, string>,
  size: string,
): string[] {
  return getDollLayerResources(bundle, selectedBody, selectedPartIds, size).map((item) => getMaterialPreviewUrl(item.resmd5))
}

export function getDollSelectedResources(
  bundle: MaterialDollBundle,
  selectedBody: MaterialResource | null,
  selectedPartIds: Record<string, string>,
  size: string,
): MaterialResource[] {
  return getDollLayerResources(bundle, selectedBody, selectedPartIds, size)
}

export function resolveFlatAssetResource(
  resources: MaterialResource[],
  topCategoryId: string,
  thumb: MaterialResource | null,
  size: string,
): MaterialResource | null {
  if (!thumb) return null
  if (topCategoryId === Category.特效) {
    return (
      resources.find((item) => getResourceExtra(item).toLowerCase().endsWith('.gif') && getResourceExtra(item).includes(`_${size}_`)) ??
      resources.find((item) => getResourceExtra(item).toLowerCase().endsWith('.gif')) ??
      null
    )
  }
  if (topCategoryId === Category.UI) {
    const imageResources = resources.filter(
      (item) => !!item.resmd5 && !getResourceExtra(item).toLowerCase().endsWith('.oui'),
    )
    const thumbBase = getResourceExtra(thumb).replace(/\.\w+$/, '')
    return (
      imageResources.find((item) => item.id === thumb.id) ??
      imageResources.find((item) => getResourceExtra(item).replace(/\.\w+$/, '') === thumbBase) ??
      imageResources.find(
        (item) => getResourceExtra(item).replace(/\.\w+$/, '') === thumbBase.replace('f_small', size),
      ) ??
      imageResources.find(
        (item) => getResourceExtra(item).replace(/\.\w+$/, '') === thumbBase.replace('f_small', '540'),
      ) ??
      thumb
    )
  }

  const thumbBase = getResourceExtra(thumb).replace('f_small', size).replace(/\.\w+$/, '')
  const matched = resources.find((item) => getResourceExtra(item).replace(/\.\w+$/, '') === thumbBase)
  if (matched) return matched

  const fallbackBase = getResourceExtra(thumb).replace('f_small', '540').replace(/\.\w+$/, '')
  return resources.find((item) => getResourceExtra(item).replace(/\.\w+$/, '') === fallbackBase) ?? thumb
}

/**
 * 解析右侧大图预览资源；`size` 与分辨率下拉选中档一致。
 */
export function resolveFlatAssetPreviewResource(
  resources: MaterialResource[],
  topCategoryId: string,
  thumb: MaterialResource | null,
  size = MATERIAL_PREVIEW_DISPLAY_SIZE,
): MaterialResource | null {
  if (!thumb) return null
  if (topCategoryId === Category.特效) {
    return (
      resources.find((item) => getResourceExtra(item).toLowerCase().endsWith('.gif') && getResourceExtra(item).includes(`_${size}_`))
      ?? resources.find((item) => getResourceExtra(item).toLowerCase().endsWith('.gif'))
      ?? thumb
    )
  }
  if (topCategoryId === Category.UI) {
    const imageResources = resources.filter(
      (item) => !!item.resmd5 && !getResourceExtra(item).toLowerCase().endsWith('.oui'),
    )
    const thumbBase = getResourceExtra(thumb).replace(/\.\w+$/, '')
    return (
      imageResources.find((item) => item.id === thumb.id) ??
      imageResources.find((item) => getResourceExtra(item).replace(/\.\w+$/, '') === thumbBase) ??
      imageResources.find(
        (item) => getResourceExtra(item).replace(/\.\w+$/, '') === thumbBase.replace('f_small', size),
      ) ??
      imageResources[0] ??
      thumb
    )
  }

  const extraText = getResourceExtra(thumb)
    .replace('f_small', size)
    .replace(/\.\w+$/, '')
  return resources.find((item) => getResourceExtra(item).replace(/\.\w+$/, '') === extraText) ?? thumb
}
